import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, Users, FolderKanban, Receipt, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  ChartCard, CompositionChart, COMPOSITION_LEGEND, DistributionBar, DonutChart,
  IncomeTrendChart, RankedBars, SimpleTable, StatTile, TrendBars, VizLegend,
  colorForKey, fmtPct, pctOf, signTone, trimTrailingEmpty, wholeMoney,
} from '../components/charts/finops-charts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtUSD = (v) => `$${wholeMoney(v).toLocaleString('en-US')}`;
const fmtUZS = (v) => `${wholeMoney(v).toLocaleString('en-US')} UZS`;

function Analytics() {
  const { t } = useTranslation('analytics');
  const MONTHS = t('common:monthsShort', { returnObjects: true });
  const now = new Date();
  const [fromMonth, setFromMonth] = useState(1);
  const [fromYear, setFromYear] = useState(now.getFullYear());
  const [toMonth, setToMonth] = useState(now.getMonth() + 1);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [currency, setCurrency] = useState('USD');
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [expandedProj, setExpandedProj] = useState(null);

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API_URL}/api/analytics`, {
        params: { from_month: fromMonth, from_year: fromYear, to_month: toMonth, to_year: toYear },
        withCredentials: true,
      });
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [fromMonth, fromYear, toMonth, toYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const fmt = currency === 'USD' ? fmtUSD : fmtUZS;
  const suf = currency === 'USD' ? '_usd' : '_uzs';

  const tabs = [
    { id: 'overview', label: t('tabs.overview'), icon: TrendingUp },
    { id: 'employees', label: t('tabs.employees'), icon: Users },
    { id: 'projects', label: t('tabs.projects'), icon: FolderKanban },
    { id: 'expenses', label: t('tabs.expenses'), icon: Receipt },
  ];

  // ── Period selector ──
  const PeriodSelector = () => (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <Label className="text-xs mb-1 block">{t('period.from')}</Label>
        <div className="flex gap-1">
          <Select value={String(fromMonth)} onValueChange={(v) => setFromMonth(Number(v))}>
            <SelectTrigger className="w-[90px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(fromYear)} onValueChange={(v) => setFromYear(Number(v))}>
            <SelectTrigger className="w-[80px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <span className="text-muted-foreground pb-1">→</span>
      <div>
        <Label className="text-xs mb-1 block">{t('period.to')}</Label>
        <div className="flex gap-1">
          <Select value={String(toMonth)} onValueChange={(v) => setToMonth(Number(v))}>
            <SelectTrigger className="w-[90px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(toYear)} onValueChange={(v) => setToYear(Number(v))}>
            <SelectTrigger className="w-[80px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Select value={currency} onValueChange={setCurrency}>
        <SelectTrigger className="w-[80px] h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="USD">USD</SelectItem>
          <SelectItem value="UZS">UZS</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  // ── Overview ──
  // Income, salaries and total expenses are near-identical here (almost all
  // income is distributed as salary), so plotting them as parallel absolute
  // series just draws one line on top of another. What actually varies is the
  // income total and how it splits — so: one trend series, plus composition.
  const OverviewTab = () => {
    if (!data) return null;
    const s = data.summary;

    const income = Number(s[`total_income${suf}`] || 0);
    const salaries = Number(s[`total_salaries${suf}`] || 0);
    const expenses = Number(s[`total_expenses${suf}`] || 0);
    const retained = Number(s[`net_profit${suf}`] || 0);

    const rows = trimTrailingEmpty(
      (s.monthly_trend || []).map(row => {
        const inc = Number(row[`income${suf}`] || 0);
        const sal = Number(row[`salaries${suf}`] || 0);
        const ope = Number(row[`expenses${suf}`] || 0);
        return {
          label: `${MONTHS[row.month - 1]} ${String(row.year).slice(2)}`,
          income: inc,
          salary: sal,
          operational: ope,
          retained: Number((inc - sal - ope).toFixed(2)),
        };
      }),
      ['income', 'salary', 'operational']
    );

    const incomeRows = rows.map(r => ({ label: r.label, value: r.income }));
    const share = (v) => {
      const p = pctOf(v, income);
      return p === null ? '—' : t('overview.shareOfIncome', { pct: fmtPct(p) });
    };

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile label={t('overview.totalIncome')} value={fmt(income)} icon={DollarSign} emphasis />
          <StatTile label={t('overview.salaries')} value={fmt(salaries)} sub={share(salaries)} icon={Users} />
          <StatTile label={t('overview.operationalExpense')} value={fmt(expenses)} sub={share(expenses)} icon={Receipt} />
          <StatTile
            label={t('overview.retained')}
            value={fmt(retained)}
            sub={share(retained)}
            tone={signTone(retained)}
            icon={TrendingUp}
          />
        </div>

        {income > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {t('overview.distributionTitle')}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {t('overview.distributionSubtitle', { amount: fmt(income) })}
            </p>
            <DistributionBar
              salary={salaries}
              operational={expenses}
              retained={retained}
              currency={currency}
            />
          </div>
        )}

        <ChartCard
          title={t('overview.incomeTrendTitle')}
          subtitle={t('overview.incomeTrendSubtitle')}
          table={
            <SimpleTable
              columns={[
                { key: 'label', label: t('common:fields.month') },
                { key: 'income', label: t('columns.income'), align: 'right' },
              ]}
              rows={rows.map(r => ({ label: r.label, income: fmt(r.income) }))}
            />
          }
        >
          <IncomeTrendChart data={incomeRows} currency={currency} height={300} />
        </ChartCard>

        <ChartCard
          title={t('overview.compositionTitle')}
          subtitle={t('overview.compositionSubtitle')}
          table={
            <SimpleTable
              columns={[
                { key: 'label', label: t('common:fields.month') },
                { key: 'salary', label: t('columns.salaries'), align: 'right' },
                { key: 'operational', label: t('columns.operational'), align: 'right' },
                { key: 'retained', label: t('columns.retained'), align: 'right' },
                { key: 'income', label: t('columns.total'), align: 'right' },
              ]}
              rows={rows.map(r => ({
                label: r.label,
                salary: fmt(r.salary),
                operational: fmt(r.operational),
                retained: fmt(r.retained),
                income: fmt(r.income),
              }))}
            />
          }
        >
          <CompositionChart data={rows} currency={currency} height={300} />
          <VizLegend items={COMPOSITION_LEGEND} />
        </ChartCard>
      </div>
    );
  };

  // ── Employees tab ──
  const EmployeesTab = () => {
    if (!data) return null;
    const employees = data.employees;

    // Each bar gets its own hue, keyed off employee_id (not row position) so the
    // ordering shifting between periods never repaints anyone.
    const chartData = employees.slice(0, 10).map(e => ({
      name: e.employee_name.length > 18 ? e.employee_name.slice(0, 18) + '…' : e.employee_name,
      value: e[`total_earnings${suf}`],
      colorKey: e.employee_id,
    }));

    return (
      <div className="space-y-6">
        {chartData.length > 0 && (
          <ChartCard
            title={t('employees.topTitle')}
            subtitle={t('employees.topSubtitle', { count: chartData.length })}
            table={
              <SimpleTable
                columns={[
                  { key: 'name', label: t('columns.employee') },
                  { key: 'value', label: t('columns.total'), align: 'right' },
                ]}
                rows={chartData.map(d => ({ name: d.name, value: fmt(d.value) }))}
              />
            }
          >
            <RankedBars data={chartData} currency={currency} colorBy height={320} />
          </ChartCard>
        )}

        {/* Employee table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t('employees.earningsTitle', { count: employees.length })}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('employees.headers.employee')}</th>
                    <th className="text-left p-3 font-medium">{t('employees.headers.position')}</th>
                    <th className="text-right p-3 font-medium">{t('employees.headers.totalEarnings')}</th>
                    <th className="text-right p-3 font-medium">{t('employees.headers.avgMonth')}</th>
                    <th className="text-center p-3 font-medium">{t('employees.headers.months')}</th>
                    <th className="text-center p-3 font-medium">{t('employees.headers.projects')}</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e) => (
                    <React.Fragment key={e.employee_id}>
                      <tr
                        className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => setExpandedEmp(expandedEmp === e.employee_id ? null : e.employee_id)}
                      >
                        <td className="p-3 font-medium flex items-center gap-1">
                          {expandedEmp === e.employee_id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {e.employee_name}
                        </td>
                        <td className="p-3 text-muted-foreground">{e.position}</td>
                        <td className={`p-3 text-right font-semibold ${e[`total_earnings${suf}`] >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {fmt(e[`total_earnings${suf}`])}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">{fmt(e[`avg_monthly${suf}`])}</td>
                        <td className="p-3 text-center">{e.months_active}</td>
                        <td className="p-3 text-center">{e.projects.length}</td>
                      </tr>
                      {/* Rendered only while expanded, so the per-employee chart
                          costs nothing for the rows nobody opened. The data rides
                          along in the analytics response — no extra request. */}
                      {expandedEmp === e.employee_id && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <div className="bg-slate-50 dark:bg-slate-800/40 px-6 py-4 grid gap-5 lg:grid-cols-2">
                              <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                  {t('employees.periodEarnings')}
                                </p>
                                <TrendBars
                                  data={(e.monthly || []).map(m => ({
                                    label: `${MONTHS[m.month - 1]} ${String(m.year).slice(2)}`,
                                    value: currency === 'USD' ? m.earnings_usd : m.earnings_uzs,
                                  }))}
                                  currency={currency}
                                  name={t('columns.income')}
                                  color={colorForKey(e.employee_id)}
                                  height={200}
                                />
                                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-500 dark:text-slate-400">
                                  <span>
                                    {t('employees.average')}{' '}
                                    <span className="font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                                      {fmt(e[`avg_monthly${suf}`])}
                                    </span>
                                  </span>
                                  <span>
                                    {t('employees.peak')}{' '}
                                    <span className="font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                                      {fmt(Math.max(0, ...(e.monthly || []).map(m => currency === 'USD' ? m.earnings_usd : m.earnings_uzs)))}
                                    </span>
                                  </span>
                                  <span>
                                    {t('employees.activeMonths')}{' '}
                                    <span className="font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                                      {e.months_active}
                                    </span>
                                  </span>
                                </div>
                              </div>

                              <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                  {t('employees.byProjects', { count: e.projects.length })}
                                </p>
                                {e.projects.length > 0 ? (
                                  <div className="space-y-1.5">
                                    {e.projects.map(p => {
                                      const v = p[`total${suf}`];
                                      const pct = pctOf(v, e[`total_earnings${suf}`]) ?? 0;
                                      return (
                                        <div key={p.project_id} className="text-xs">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-slate-600 dark:text-slate-300 truncate">{p.project_name}</span>
                                            <span className="ml-auto pl-2 font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                                              {fmt(v)}
                                            </span>
                                            <span className="w-11 text-right text-slate-400 dark:text-slate-500 tabular-nums">
                                              {pct.toFixed(0)}%
                                            </span>
                                          </div>
                                          <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                            <div
                                              className="h-full rounded-full"
                                              style={{ width: `${Math.max(pct, 0)}%`, background: colorForKey(e.employee_id) }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-xs text-slate-400 dark:text-slate-500">
                                    {t('employees.noProjects')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {employees.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{t('employees.empty')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Projects tab ──
  const ProjectsTab = () => {
    if (!data) return null;
    const projects = data.projects;

    // Per project, income splits into salaries / operational / retained the same
    // way it does company-wide — so stack the split instead of putting three
    // near-equal absolute bars side by side.
    const chartData = projects.slice(0, 10).map(p => {
      const inc = Number(p[`total_income${suf}`] || 0);
      const sal = Number(p[`total_salaries${suf}`] || 0);
      const ope = Number(p[`total_expenses${suf}`] || 0);
      return {
        label: p.project_name.length > 12 ? p.project_name.slice(0, 12) + '…' : p.project_name,
        income: inc,
        salary: sal,
        operational: ope,
        retained: Number((inc - sal - ope).toFixed(2)),
      };
    });

    return (
      <div className="space-y-6">
        {chartData.length > 0 && (
          <ChartCard
            title={t('projects.chartTitle')}
            subtitle={t('projects.chartSubtitle')}
            table={
              <SimpleTable
                columns={[
                  { key: 'label', label: t('columns.project') },
                  { key: 'salary', label: t('columns.salaries'), align: 'right' },
                  { key: 'operational', label: t('columns.operational'), align: 'right' },
                  { key: 'retained', label: t('columns.retained'), align: 'right' },
                  { key: 'income', label: t('columns.income'), align: 'right' },
                ]}
                rows={chartData.map(d => ({
                  label: d.label,
                  salary: fmt(d.salary),
                  operational: fmt(d.operational),
                  retained: fmt(d.retained),
                  income: fmt(d.income),
                }))}
              />
            }
          >
            <CompositionChart data={chartData} currency={currency} height={320} />
            <VizLegend items={COMPOSITION_LEGEND} />
          </ChartCard>
        )}

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t('projects.performanceTitle', { count: projects.length })}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('projects.headers.project')}</th>
                    <th className="text-center p-3 font-medium">{t('projects.headers.status')}</th>
                    <th className="text-right p-3 font-medium">{t('projects.headers.income')}</th>
                    <th className="text-right p-3 font-medium">{t('projects.headers.expenses')}</th>
                    <th className="text-right p-3 font-medium">{t('projects.headers.salaries')}</th>
                    <th className="text-right p-3 font-medium">{t('projects.headers.netProfit')}</th>
                    <th className="text-center p-3 font-medium">{t('projects.headers.months')}</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map(p => {
                    const isOpen = expandedProj === p.project_id;
                    // Per-month split for this project, same shape the
                    // company-wide composition chart consumes.
                    const monthRows = (p.monthly || []).map(m => {
                      const inc = currency === 'USD' ? m.income_usd : m.income_uzs;
                      const sal = currency === 'USD' ? m.salaries_usd : m.salaries_uzs;
                      const ope = currency === 'USD' ? m.expenses_usd : m.expenses_uzs;
                      return {
                        label: `${MONTHS[m.month - 1]} ${String(m.year).slice(2)}`,
                        income: inc,
                        salary: sal,
                        operational: ope,
                        retained: Number((inc - sal - ope).toFixed(2)),
                      };
                    });

                    return (
                    <React.Fragment key={p.project_id}>
                    <tr
                      className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => setExpandedProj(isOpen ? null : p.project_id)}
                    >
                      <td className="p-3 font-medium">
                        <span className="inline-flex items-center gap-1">
                          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {p.project_name}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          p.status === 'completed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>{t(`projects.status.${p.status}`, { defaultValue: p.status })}</span>
                      </td>
                      <td className="p-3 text-right text-green-600">{fmt(p[`total_income${suf}`])}</td>
                      <td className="p-3 text-right text-red-500">{fmt(p[`total_expenses${suf}`])}</td>
                      <td className="p-3 text-right text-blue-500">{fmt(p[`total_salaries${suf}`])}</td>
                      <td className={`p-3 text-right font-semibold ${signTone(p[`net_profit${suf}`]) === 'negative' ? 'text-red-500' : 'text-green-600'}`}>
                        {wholeMoney(p[`net_profit${suf}`]) < 0 ? '-' : ''}{fmt(Math.abs(wholeMoney(p[`net_profit${suf}`])))}
                      </td>
                      <td className="p-3 text-center">{p.months_active}</td>
                    </tr>

                    {/* Rendered only while expanded — collapsed rows cost nothing.
                        The data rides along in the analytics response. */}
                    {isOpen && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <div className="bg-slate-50 dark:bg-slate-800/40 px-6 py-4 grid gap-5 lg:grid-cols-3">
                            <div className="lg:col-span-2">
                              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                {t('projects.monthlySplit')}
                              </p>
                              <CompositionChart data={monthRows} currency={currency} height={220} />
                              <VizLegend items={COMPOSITION_LEGEND} />
                            </div>

                            <div>
                              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                {t('projects.monthlyIncome')}
                              </p>
                              <SimpleTable
                                columns={[
                                  { key: 'label', label: t('common:fields.month') },
                                  { key: 'income', label: t('columns.income'), align: 'right' },
                                  { key: 'retained', label: t('columns.retained'), align: 'right' },
                                ]}
                                rows={monthRows.map(r => ({
                                  label: r.label,
                                  income: fmt(r.income),
                                  retained: fmt(r.retained),
                                }))}
                              />
                              {monthRows.length > 0 && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                  {t('projects.avgIncome')}{' '}
                                  <span className="font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                                    {fmt(monthRows.reduce((s, r) => s + r.income, 0) / monthRows.length)}
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                  {projects.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">{t('projects.empty')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Expenses tab ──
  const ExpensesTab = () => {
    if (!data) return null;
    const { by_category, by_type, monthly_trend } = data.expenses;

    // Ranked bars rather than a pie: categories are usually more than the ~6 a
    // pie can carry, and the values here are often close enough that arc angles
    // can't be compared reliably.
    const catData = by_category
      .map(c => ({
        name: c.category,
        value: currency === 'USD' ? c.total_usd : c.total_uzs,
      }))
      .sort((a, b) => b.value - a.value);

    const trendData = trimTrailingEmpty(
      monthly_trend.map(row => ({
        label: `${MONTHS[row.month - 1]} ${String(row.year).slice(2)}`,
        value: currency === 'USD' ? row.total_usd : row.total_uzs,
      })),
      ['value']
    );

    return (
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          {catData.length > 0 && (
            <ChartCard
              title={t('expenses.byCategoryTitle')}
              subtitle={t('expenses.byCategorySubtitle')}
              table={
                <SimpleTable
                  columns={[
                    { key: 'name', label: t('columns.category') },
                    { key: 'value', label: t('columns.total'), align: 'right' },
                  ]}
                  rows={catData.map(c => ({ name: c.name, value: fmt(c.value) }))}
                />
              }
            >
              <DonutChart data={catData} currency={currency} height={220} centerLabel={t('columns.total')} />
            </ChartCard>
          )}

          {/* By type */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">{t('expenses.byType')}</CardTitle></CardHeader>
            <CardContent>
              {(() => {
                const typeTotal = by_type.reduce((s, x) => s + (currency === 'USD' ? x.total_usd : x.total_uzs), 0);
                return (
              <div className="space-y-3 pt-2">
                {by_type.map(et => {
                  const val = currency === 'USD' ? et.total_usd : et.total_uzs;
                  const pct = typeTotal > 0 ? (val / typeTotal * 100) : 0;
                  return (
                    <div key={et.expense_type}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize">{t(`expenses.types.${et.expense_type}`, { defaultValue: et.expense_type.replace('_', ' ') })}</span>
                        <span className="font-medium">{fmt(val)} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {by_type.length === 0 && <p className="text-sm text-muted-foreground text-center">{t('expenses.noExpenses')}</p>}
              </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Expense trend */}
        {trendData.length > 0 && (
          <ChartCard
            title={t('expenses.trendTitle')}
            table={
              <SimpleTable
                columns={[
                  { key: 'label', label: t('common:fields.month') },
                  { key: 'value', label: t('columns.expense'), align: 'right' },
                ]}
                rows={trendData.map(row => ({ label: row.label, value: fmt(row.value) }))}
              />
            }
          >
            <TrendBars data={trendData} currency={currency} name={t('columns.expense')} height={260} />
          </ChartCard>
        )}

        {/* Category table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t('expenses.breakdownTitle')}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('expenses.headers.category')}</th>
                    <th className="text-right p-3 font-medium">{t('expenses.headers.total')}</th>
                    <th className="text-center p-3 font-medium">{t('expenses.headers.count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {by_category.map(c => (
                    <tr key={c.category} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="p-3 capitalize font-medium">{c.category}</td>
                      <td className="p-3 text-right font-semibold text-red-500">{fmt(currency === 'USD' ? c.total_usd : c.total_uzs)}</td>
                      <td className="p-3 text-center">{c.count}</td>
                    </tr>
                  ))}
                  {by_category.length === 0 && (
                    <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">{t('expenses.empty')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>
        <PeriodSelector />
      </div>

      {/* Tabs: scroll sideways on narrow screens instead of widening the page */}
      <div className="tab-strip flex gap-1 border-b overflow-x-auto whitespace-nowrap">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'employees' && <EmployeesTab />}
          {activeTab === 'projects' && <ProjectsTab />}
          {activeTab === 'expenses' && <ExpensesTab />}
        </>
      )}
    </div>
  );
}

export default Analytics;
